import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Bell, Loader } from 'lucide-react';
import { produksjonAPI } from '../../services/api';
import { queryKeys } from '../../lib/queryKeys';
import type { VarslingsstatusRad, VarslingsstatusVerdi } from '../../services/api/types';
import { Badge } from '../ui/badge';
import SectionTitle from './SectionTitle';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

/**
 * Admin-visning av leveringsstatus for push-varsler per bemannet person (P3).
 * Se Docs/PUSH_ONLY_ARCHITECTURE.md §2.3/§2.5 og Docs/NOTIFICATIONS.md §4.
 *
 * Datakilde: GET /api/produksjon/:id/varslingsstatus. Kun for admin - vises kun
 * fra ProductionDetail.tsx når isAdmin() er true (samme sjekk som resten av siden).
 */
interface VarslingsstatusPanelProps {
  produksjonId: number;
}

const STATUS_LABEL: Record<VarslingsstatusVerdi, string> = {
  venter: 'Venter',
  pushet: 'Pushet',
  mottatt: 'Mottatt',
  sett: 'Sett',
  besvart: 'Besvart',
  kan_ikke_nas: 'Ikke innlogget',
  ikke_nadd: 'Ikke nådd',
};

// Statuser som skal fremheves visuelt (varselfarge) - krever oppfølging fra admin.
const VARSEL_STATUSER: VarslingsstatusVerdi[] = ['kan_ikke_nas', 'ikke_nadd'];

const formatTidspunkt = (iso: string | null): string => {
  if (!iso) {
    return '-';
  }
  return new Date(iso).toLocaleString('nb-NO', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const StatusBadge: React.FC<{ status: VarslingsstatusVerdi }> = ({ status }) => {
  const erVarsel = VARSEL_STATUSER.includes(status);
  return (
    <Badge
      variant={erVarsel ? 'destructive' : 'secondary'}
      className="inline-flex items-center gap-1"
    >
      {erVarsel && <AlertTriangle size={12} />}
      {STATUS_LABEL[status]}
    </Badge>
  );
};

const VarslingsstatusPanel: React.FC<VarslingsstatusPanelProps> = ({ produksjonId }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.productions.varslingsstatus(produksjonId),
    queryFn: () => produksjonAPI.getVarslingsstatus(produksjonId),
    enabled: Number.isFinite(produksjonId) && produksjonId > 0,
  });

  const rader: VarslingsstatusRad[] = data?.varslingsstatus || [];

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-4">
        <Loader size={16} className="animate-spin" />
        <span>Laster varslingsstatus...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-red-600 py-4">
        <AlertTriangle size={16} />
        <span>Kunne ikke laste varslingsstatus</span>
      </div>
    );
  }

  if (rader.length === 0) {
    return <p className="text-gray-500 py-4">Ingen bemannede ennå</p>;
  }

  return (
    <div className="bg-white rounded-xl shadow-md p-6">
      <div className="mb-4">
        <SectionTitle icon={Bell}>Varslingsstatus</SectionTitle>
      </div>
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Navn</TableHead>
              <TableHead>Talent</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Forsøk</TableHead>
              <TableHead>Siste push</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rader.map((rad) => (
              <TableRow
                key={rad.bemanningId}
                className={VARSEL_STATUSER.includes(rad.status) ? 'bg-red-50' : undefined}
              >
                <TableCell>{rad.navn}</TableCell>
                <TableCell>{rad.talentNavn}</TableCell>
                <TableCell>
                  <StatusBadge status={rad.status} />
                </TableCell>
                <TableCell>{rad.attempts}</TableCell>
                <TableCell>{formatTidspunkt(rad.lastPushAt)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default VarslingsstatusPanel;
